/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
Copyright (c) 2026 Dynamic IOT Data, Inc. All rights reserved.

@author Andrew Craig
*/

import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { PublishTopic } from '../../events';
import { VisionMap } from '../../map/map';
import { VisionMapMarker } from '../../map/marker';
import { IPubSubSubscriber } from '../../pubsub';
import { AssetTelemetry } from '../../service/latest';
import { VisionAreaSearchWindow } from '../../window/areasearch';
import { VisionAVLTab2 } from './avl_tab';
import { VisionApplication } from '../../application';
import { MapCountyController } from './county_controller';
import { Measurement } from '../../components/measurement';

class MapController {
    controlDiv: HTMLDivElement;
    tab: MapPanel;

    constructor(tab: MapPanel) {
        this.tab = tab;
        this.controlDiv = document.createElement('div');

        const controlButton = document.createElement('button');
        controlButton.className = 'btn';
        controlButton.innerHTML = '<i class="fa fa-search"></i> Area Search';
        this.controlDiv.appendChild(controlButton);

        // Setup the click event listeners
        controlButton.addEventListener('click', () => {
            this.tab.startDrawing();
        });
    }
}

export class MapPanel implements IPubSubSubscriber {
    identifier = 'ea6a1fe0-a807-45a3-8fe1-de6baa0aa1e0';
    parent: VisionAVLTab2;
    map!: VisionMap;
    drawingManager!: google.maps.drawing.DrawingManager;
    markerCache: Map<string, VisionMapMarker>;
    win: VisionAreaSearchWindow | undefined;
    showOnlyOnMap = false;

    cluster!: MarkerClusterer;

    isDrawing = false;
    drawingEnabled = false;
    rectangle: google.maps.Rectangle | null = null;
    startLatLng: google.maps.LatLng | null = null;
    currentRoadSegment: google.maps.Polyline | null = null;
    infoWindow: google.maps.InfoWindow | null = null;

    public startDrawing() {
        this.drawingEnabled = true;
    }

    constructor(parent: VisionAVLTab2) {
        this.parent = parent;
        this.markerCache = new Map<string, VisionMapMarker>();
        this.win = undefined;

        this.parent.layout.cells('c').setText('Map');
        this.parent.layout.cells('c').attachHTMLString('<div id="map_panel_id" style="width:100%;height:100%"></div>');
        this.parent.layout.cells('c').hideHeader();

        this.map = new VisionMap('map_panel_id');
        this.map
            .init('avl_map', this.parent.app.organizationService.dto?.latitude, this.parent.app.organizationService.dto?.longitude)
            .then(() => {
                this.parent.app.pubsub.subscribe(PublishTopic.UPDATE_ASSET_TELEMETRY, this);
                this.parent.app.pubsub.subscribe(PublishTopic.MARKER_CLICKED, this);
                this.parent.app.pubsub.subscribe(PublishTopic.FOLDER_SWITCH, this);
                this.parent.app.pubsub.subscribe(PublishTopic.ONLY_SHOW_ON_MAP, this);
                this.parent.app.pubsub.subscribe(PublishTopic.MARKER_CREATED, this);

                const controller = new MapController(this);
                this.map.map.controls[google.maps.ControlPosition.TOP_LEFT].push(controller.controlDiv);

                this.map.map.addListener('mousedown', (e: google.maps.MapMouseEvent) => {
                    if (!this.drawingEnabled || this.isDrawing) {
                        return;
                    }
                    this.startLatLng = e.latLng;
                    this.isDrawing = true;

                    this.map.map.setOptions({ draggable: false });

                    // Remove previous rectangle
                    if (this.rectangle) {
                        this.rectangle.setMap(null);
                        this.rectangle = null;
                    }

                    this.rectangle = new google.maps.Rectangle({
                        bounds: new google.maps.LatLngBounds(this.startLatLng, this.startLatLng),
                        editable: true,
                        draggable: true,
                        map: this.map.map,
                    });
                });

                // Mouse move updates rectangle size
                this.map.map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
                    if (!this.isDrawing || !this.startLatLng || !this.rectangle) {
                        return;
                    }
                    if (e.latLng != null) {
                        const bounds = new google.maps.LatLngBounds(this.startLatLng, e.latLng);
                        this.rectangle.setBounds(bounds);
                    }
                });

                // Mouse up finishes rectangle
                document.addEventListener('mouseup', (e) => {
                    if (this.isDrawing && this.drawingEnabled) {
                        this.isDrawing = false;
                        this.drawingEnabled = false;
                        this.map.map.setOptions({ draggable: true });

                        if (!this.rectangle) return;

                        const bounds = this.rectangle.getBounds();
                        if (!bounds) return;

                        this.win = new VisionAreaSearchWindow(this.parent.app.windowManager);
                        this.win.setArea(this.rectangle);
                    }
                });

                this.cluster = new MarkerClusterer({});
                this.cluster.setMap(this.map.map);

                /*
                google.maps.event.addListener(this.drawingManager, 'overlaycomplete', (event: { type: google.maps.drawing.OverlayType; overlay: unknown }) => {
                    this.win = new VisionAreaSearchWindow(this.parent.app.windowManager);

                    this.win.setArea(event.overlay as google.maps.Rectangle);
                    this.stopDrawing();
                });
                */

                this.map.map.addListener('zoom_changed', () => {
                    //console.log('ZOOM CHANGED: ' + this.map.map.getZoom());
                    this.parent.app.pubsub.publish(PublishTopic.MAP_ZOOM_CHANGE, this.map.map.getZoom());
                });

                // User clicks on the map.
                this.map.map.addListener('click', (e: google.maps.MapMouseEvent) => {
                    const domEvent = e.domEvent as MouseEvent;
                    if (domEvent.ctrlKey && e.latLng) {
                        this.mapClicked(e);
                    }
                });

                this.stopDrawing();

                this.map.map.addListener('idle', () => {
                    if (this.showOnlyOnMap == true) {
                        this.onlyShowVisible();
                    }
                });

                const state = localStorage.getItem('state');
                if (state == 'ia' || state == 'all') {
                    new MapCountyController(this);
                }
            })
            .then(() => {
                function waitForGoogleMaps(): Promise<void> {
                    return new Promise((resolve) => {
                        if (window.google && google.maps) {
                            resolve();
                        } else {
                            const interval = setInterval(() => {
                                if (window.google && google.maps) {
                                    clearInterval(interval);
                                    resolve();
                                }
                            }, 50); // check every 50ms
                        }
                    });
                }

                waitForGoogleMaps().then(() => {
                    this.parent.app.pubsub.publish(PublishTopic.AVL_MAP_LOADED);
                    VisionApplication.getInstance().setMapWorkspace(this.map.map);
                });
            });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    notify(msg: string, data?: any): void {
        switch (msg) {
            case PublishTopic.MARKER_CREATED: {
                const settings = VisionApplication.getInstance().userService.settings;
                if (settings != null && settings.cluster == true) {
                    this.cluster.addMarker((data as VisionMapMarker).marker);
                }
                break;
            }
            case PublishTopic.UPDATE_ASSET_TELEMETRY:
                this.updateTelemetry(data[0] as AssetTelemetry[], data[1]);
                break;

            case PublishTopic.MARKER_CLICKED:
                this.markerClicked(data as VisionMapMarker);
                break;

            case PublishTopic.FOLDER_SWITCH:
                this.clearMarkers();
                break;

            case PublishTopic.ONLY_SHOW_ON_MAP: {
                this.showOnlyOnMap = data as boolean;
                if (this.showOnlyOnMap == false) {
                    this.showAllAssets();
                } else {
                    this.onlyShowVisible();
                }
                break;
            }
        }
    }

    private showAllAssets() {
        this.parent.assetTree.tree.forEachRow((id) => {
            this.parent.assetTree.tree.setRowHidden(id, false);
        });
    }

    private onlyShowVisible() {
        this.parent.assetTree.tree.forEachRow((id) => {
            const asset = this.parent.app.latestService.assetCache.get(id);

            if (asset != undefined) {
                const lon = asset.position.coordinates[0];
                const lat = asset.position.coordinates[1];
                const pos = new google.maps.LatLng(lat, lon);

                if (this.map.map.getBounds()?.contains(pos)) {
                    this.parent.assetTree.tree.setRowHidden(id, false);
                } else {
                    this.parent.assetTree.tree.setRowHidden(id, true);
                }
            } else {
                this.parent.assetTree.tree.setRowHidden(id, true);
            }
        });
    }

    /** Clear the markers on switching Folders
     *
     */
    private clearMarkers() {
        this.markerCache.forEach((item) => {
            if (item && item.marker) {
                item.marker.setMap(null);
            }
            if (item && item.tailLine) {
                item.tailLine.setMap(null);
            }
        });
        this.markerCache.clear();
    }

    private markerClicked(marker: VisionMapMarker) {
        console.log(marker);
        this.parent.assetTree.tree.selectRowById(marker.id, false, true, false);
    }

    private updateTelemetry(assets: AssetTelemetry[], doZoom = false) {
        assets.forEach((item) => {
            const cached = this.markerCache.get(item.id);
            if (cached) {
                cached.update(item);
            } else {
                const marker = new VisionMapMarker(this.map, item);
                this.markerCache.set(item.id, marker);

                /*
                const position = { lat: -31.56391, lng: 147.154312 };
                const pinGlyph = new google.maps.marker.PinElement({
                    glyph: item.name,
                    glyphColor: 'white',
                });
                const newmarker = new google.maps.marker.AdvancedMarkerElement({
                    position,
                    content: pinGlyph.element,
                });

                const clickMarker = new google.maps.Marker({
                    position: position,
                    map: this.map.map,
                    clickable: true,
                    zIndex: 99,
                });

                this.cluster.addMarker(clickMarker);
                console.log(clickMarker.getPosition());
                */
            }
        });

        if (doZoom == true) {
            this.zoomToAll();
        }
    }

    private zoomToAll(): void {
        const bounds = new google.maps.LatLngBounds();
        console.log(this.markerCache.size);
        this.markerCache.forEach((item) => {
            const pos = item.position;
            if (pos) {
                bounds.extend(pos);
            }
        });
        this.map.map.fitBounds(bounds);
    }

    public zoomTo(assetid: string) {
        const asset = this.markerCache.get(assetid);
        if (asset) {
            this.map.map.panTo(asset.position);
        }
    }

    public zoomToLevel(assetid: string, level: number) {
        const asset = this.markerCache.get(assetid);
        if (asset) {
            this.map.map.setZoom(level);
            this.map.map.panTo(asset.position);
        }
    }

    zoomToLocationAndLevel(location: google.maps.LatLng, level: number) {
        this.map.map.setZoom(level);
        this.map.map.panTo(location);
    }

    public zoomToLocation(location: google.maps.LatLng) {
        this.map.map.panTo(location);
    }

    public stopDrawing() {
        this.drawingEnabled = false;
        this.isDrawing = false;
        this.map.map.setOptions({ draggable: true });
        this.rectangle?.setMap(null);
        this.rectangle = null;
    }

    public tempCircle(location: google.maps.LatLng) {
        const icon = {
            path: google.maps.SymbolPath.CIRCLE,
            strokeColor: '#FF0000',
            fillOpacity: 0.0,
            strokeWeight: 1,
            scale: 32 / 2, // Set the scale to half of the desired diameter in pixels
        };

        const marker = new google.maps.Marker({
            position: location,
            map: this.map.map,
            icon: icon,
        });

        setTimeout(() => {
            if (marker) {
                marker.setMap(null);
            }
        }, 5000);
    }

    private mapClicked(e: google.maps.MapMouseEvent) {
        if (e.latLng == null) {
            return;
        }
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        console.log(`Control-clicked at: Latitude ${lat}, Longitude ${lng}`);
        VisionApplication.getInstance()
            .roadService.getRoadSegment(lat, lng)
            .then((road) => {
                const path = road.geometry.coordinates.map((coord) => ({
                    lng: coord[0],
                    lat: coord[1],
                }));

                if (this.currentRoadSegment) {
                    this.currentRoadSegment.setMap(null);
                }
                if (this.infoWindow) {
                    this.infoWindow.close();
                }

                this.currentRoadSegment = new google.maps.Polyline({
                    path: path,
                    geodesic: true,
                    strokeColor: '#FF0000',
                    strokeOpacity: 1.0,
                    strokeWeight: 2,
                });
                this.currentRoadSegment.setMap(this.map.map);
                this.infoWindow = new google.maps.InfoWindow({
                    content: `
        <div style="
            font-family: Arial, sans-serif;
            width: 220px;
            padding: 8px;
            color: #333;
        ">
            <div style="
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 8px;
                color: #1a73e8;
                border-bottom: 1px solid #ddd;
                padding-bottom: 6px;
            ">
                ${road.roadName ?? 'Unnamed Road'}
            </div>

            <div style="line-height: 1.6;">
                <div>
                    <span style="font-weight:bold;">Speed:</span>
                    ${road.maxspeed ? road.maxspeed : 'N/A'}
                </div>

                <div>
                    <span style="font-weight:bold;">Surface:</span>
                    ${road.surface ?? 'N/A'}
                </div>

                <div>
                    <span style="font-weight:bold;">Lanes:</span>
                    ${road.lanes ?? 'N/A'}
                </div>
                
            </div>
        </div>
    `,
                    position: e.latLng,
                });
                this.infoWindow.open(this.map.map);
                this.infoWindow.addListener('closeclick', () => {
                    if (this.currentRoadSegment) {
                        this.currentRoadSegment.setMap(null);
                    }
                });
            });
    }
}
